<template>
  <wui-modal
    :size="40"
    :closable="false"
    class="!w-[520px] !max-w-[calc(100vw-32px)] phone:!w-[360px] !max-h-[min(560px,80vh)] flex flex-col overflow-hidden"
  >
    <template #header>
      <div class="flex justify-between items-center gap-2 select-none w-full">
        <p
          class="text-heading-sb-18-24 text-fg-base-fg3 min-w-0 wrap-break-word"
          data-test="appointment-statistic-title"
        >
          {{ title }}
        </p>
        <wui-btn
          icon
          variant="neut"
          state="alpha"
          :size="40"
          prepend-icon="closeRegularMdM"
          data-test="appointment-statistic-close"
          @click="onClose"
        />
      </div>
    </template>

    <div class="flex flex-wrap gap-x-3 gap-y-1 pb-4 text-fg-base-fg10 shrink-0">
      <span class="text-body-m-15-22">
        {{ datetime }}
      </span>
      <span
        v-if="duration"
        class="text-body-r-15-22 text-fg-base-fg22"
      >
        {{ duration }}
      </span>
    </div>

    <div
      v-if="callRecordEvents.length"
      class="flex pb-5 shrink-0"
    >
      <wui-btn
        variant="neut"
        state="tonal"
        :size="32"
        prepend-icon="recordM"
        data-test="appointment-record-download"
        @click="onDownloadRecords"
      >
        {{ downloadRecordsLabel }}
      </wui-btn>
    </div>

    <div class="mx-1 border-t border-bor-nav shrink-0" />

    <div class="py-1 shrink-0">
      <div class="mt-2 flex items-center gap-x-2 rounded-12 bg-bg-base-bg3 p-2.5">
        <wui-icon
          name="searchM"
          class="h-5! w-5! shrink-0 text-fg-base-fg32"
        />
        <input
          :value="searchQuery"
          class="h-5 w-full min-w-0 outline-none text-body-r-14-20"
          type="text"
          data-test="appointment-statistic-search"
          :placeholder="$t('chat.searchParticipantOrAction')"
          @input="onSearchInput"
        >
        <wui-btn
          v-if="searchQuery"
          icon
          variant="neut"
          state="text"
          :size="24"
          prepend-icon="clear"
          data-test="appointment-statistic-search-clear"
          :title="$t('common.clear')"
          @click="clearSearch"
        />
      </div>
    </div>

    <main
      v-if="calls.length"
      ref="listRef"
      class="py-1 overflow-y-auto overscroll-contain will-change-scroll min-h-0 grow"
      :style="lockedListHeightPx ? { height: `${lockedListHeightPx}px` } : undefined"
      data-test="appointment-statistic-list"
    >
      <appointment-statistic-row
        v-for="(item, index) in filteredCalls"
        :key="`${item.participantPServed}-${item.timestamp}-${index}`"
        :data="item"
        @select-participant="onSelectParticipant"
      />
      <p
        v-if="searchQuery && !filteredCalls.length"
        class="px-4 py-6 text-center text-body-r-14-20 text-fg-base-fg22"
        data-test="appointment-statistic-empty"
      >
        {{ t('common.nothingFound') }}
      </p>
    </main>
  </wui-modal>
</template>

<script setup lang="ts">
import { CloseDialogFn, closeDialogKey, safeInject, WuiBtn, WuiIcon, WuiModal } from '@wui/common-library'
import {
  HistoryMessageItem,
  MessageCallAppointmentEnd,
  MessageCallAppointmentStart,
} from '@wui/im'
import { computed, ref } from 'vue'

import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'


import { useLocalization } from '@/shared/composables/localization'
import { downloadUrlsSequentially } from '@/shared/utils/download-files'
import { getFullURL, getPathNameFromURL } from '@/shared/utils/url'

import {
  CallStatistic,
  getAppointmentDateTimeText,
  getAppointmentDurationText,
  getCallRecordEvents,
  matchesCallStatisticSearch,
} from '../'

import AppointmentStatisticRow from './appointment-statistic-row.vue'

const props = defineProps<{
  event: HistoryMessageItem<MessageCallAppointmentEnd | MessageCallAppointmentStart>,
  message: string
  isModal?: boolean
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const searchQuery = ref('')
const selectedParticipantPServed = ref<string | null>(null)
const listRef = ref<HTMLElement | null>(null)
const lockedListHeightPx = ref<number | null>(null)
const { t } = useLocalization()

const applySearchQuery = (value: string) => {
  if (!searchQuery.value && value && listRef.value) {
    lockedListHeightPx.value = listRef.value.offsetHeight
  }

  if (!value) {
    lockedListHeightPx.value = null
    selectedParticipantPServed.value = null
  }

  searchQuery.value = value
}

const onSearchInput = (event: Event) => {
  selectedParticipantPServed.value = null
  applySearchQuery((event.target as HTMLInputElement).value)
}

const onSelectParticipant = (payload: { name: string, pServed?: string }) => {
  selectedParticipantPServed.value = payload.pServed ?? null
  applySearchQuery(payload.name)
}

const clearSearch = () => {
  applySearchQuery('')
}

let closeDialog: undefined | CloseDialogFn<undefined>
if (props.isModal) {
  closeDialog = safeInject(closeDialogKey)
}

const onClose = () => {
  if (props.isModal && closeDialog) {
    closeDialog(undefined)
  } else {
    emit('close')
  }
}

const { findRoomMessages } = chatModel.useChatMessageStore()
const { findByPServed: findRoom } = useStoreRooms()

const title = computed(() => props.message)

const isParticipantTemporary = (participantPServed?: string): boolean => {
  if (!participantPServed) {
    return false
  }

  const room = findRoom(props.event.item.pServed)
  return Boolean(room?.contacts?.find(contact => contact.id === participantPServed)?.temporary)
}

const events = computed(() => findRoomMessages(props.event.item.pServed))
const callRecordEvents = computed(() => {
  return getCallRecordEvents(events.value, props.event.item.confId)
})

const downloadRecordsLabel = computed(() => {
  return callRecordEvents.value.length > 1
    ? t('chat.downloadCallRecords', { count: callRecordEvents.value.length })
    : t('chat.downloadCallRecord')
})

const onDownloadRecords = async () => {
  const items = callRecordEvents.value.map(recordEvent => {
    const url = recordEvent.item.callRecordUrl ?? ''
    const filename = url.split('/').at(-1)
    return filename
      ? { url: getFullURL(getPathNameFromURL(url)), filename }
      : { url: getFullURL(getPathNameFromURL(url)) }
  })

  await downloadUrlsSequentially(items)
}

const calls = computed<CallStatistic[]>(() => {
  const statistic = (props.event.item as MessageCallAppointmentEnd).conferenceStatistic?.calls
  if (!statistic) {
    return []
  }

  const result: CallStatistic[] = []
  Object.values(statistic)
    .forEach(call => {
      if (call.isCallAnswered) {
        if (call.timestampCallAnswered) {
          result.push({
            isCallAnswered: true,
            timestamp: call.timestampCallAnswered,
            participantName: call.participantName,
            participantPServed: call.participantPServed,
            inviterName: call.inviterName,
            isTemporary: isParticipantTemporary(call.participantPServed),
          })
        }
      }

      if (call.isCallEnded) {
        result.push({
          isCallEnded: true,
          timestamp: call.timestampCallEnded,
          participantName: call.participantName,
          participantPServed: call.participantPServed,
          inviterName: call.inviterName,
          isTemporary: isParticipantTemporary(call.participantPServed),
        })
      }

      if (call.isCallMissed || call.timestampCallMissed) {
        result.push({
          isCallMissed: true,
          timestamp: call.timestampCallMissed,
          participantName: call.participantName,
          participantPServed: call.participantPServed,
          inviterName: call.inviterName,
          isTemporary: isParticipantTemporary(call.participantPServed),
        })
      }

      if (call.isCallRejected || call.timestampCallRejected) {
        result.push({
          isCallRejected: true,
          timestamp: call.timestampCallRejected,
          participantName: call.participantName,
          participantPServed: call.participantPServed,
          inviterName: call.inviterName,
          isTemporary: isParticipantTemporary(call.participantPServed),
        })
      }
    })

  return result.sort((a, b) => a.timestamp - b.timestamp)
})

const filteredCalls = computed(() => {
  if (selectedParticipantPServed.value) {
    return calls.value.filter(call => call.participantPServed === selectedParticipantPServed.value)
  }

  return calls.value.filter(call => matchesCallStatisticSearch(call, searchQuery.value))
})

const datetime = computed(() => getAppointmentDateTimeText(props.event))
const duration = computed(() => getAppointmentDurationText(props.event))
</script>
